import { useState, useEffect, useMemo } from 'react';
import { useCampaigns } from './useCampaigns';
import { useLeads } from './useLeads';
import { useTelnyxAgents } from './useTelnyxAgents';

export interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  type: 'campaign' | 'lead' | 'agent';
  href: string;
  icon?: string;
}

export const useGlobalSearch = (query: string) => {
  const { campaigns } = useCampaigns();
  const { leads } = useLeads();
  const { agents } = useTelnyxAgents();
  const [loading, setLoading] = useState(false);

  const results = useMemo(() => {
    if (!query || query.length < 2) return [];
    
    const searchResults: SearchResult[] = [];
    const lowerQuery = query.toLowerCase();

    // Search campaigns
    campaigns.forEach(campaign => {
      if (campaign.name?.toLowerCase().includes(lowerQuery)) {
        searchResults.push({
          id: `campaign-${campaign.id}`,
          title: campaign.name,
          subtitle: `${campaign.status} • ${campaign.total_leads || 0} leads`,
          type: 'campaign',
          href: `/campaigns/${campaign.id}`,
          icon: '📋'
        });
      }
    });

    // Search leads
    leads.forEach(lead => {
      if (
        lead.contact_name?.toLowerCase().includes(lowerQuery) ||
        lead.email?.toLowerCase().includes(lowerQuery) ||
        lead.phone?.includes(query) ||
        lead.company_name?.toLowerCase().includes(lowerQuery)
      ) {
        searchResults.push({
          id: `lead-${lead.id}`,
          title: lead.contact_name || lead.email || 'Unknown Lead',
          subtitle: `${lead.company_name ? lead.company_name + ' • ' : ''}${lead.phone}`,
          type: 'lead',
          href: `/leads?search=${encodeURIComponent(lead.contact_name || lead.email || '')}`,
          icon: '👤'
        });
      }
    });

    // Search agents
    agents.forEach(agent => {
      if (
        agent.name?.toLowerCase().includes(lowerQuery) ||
        agent.description?.toLowerCase().includes(lowerQuery)
      ) {
        searchResults.push({
          id: `agent-${agent.id}`,
          title: agent.name,
          subtitle: agent.description?.substring(0, 80) + '...',
          type: 'agent',
          href: `/agents?id=${agent.id}`,
          icon: '🤖'
        });
      }
    });

    // Limit results and sort by relevance (exact matches first)
    return searchResults
      .sort((a, b) => {
        const aExact = a.title.toLowerCase().startsWith(lowerQuery);
        const bExact = b.title.toLowerCase().startsWith(lowerQuery);
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        return 0;
      })
      .slice(0, 10);
  }, [query, campaigns, leads, agents]);

  return { results, loading };
};